/**
 * Passkey Sign-In button — passwordless login with Face ID / Touch ID / Windows Hello.
 * Uses @simplewebauthn/browser to run the WebAuthn ceremony.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import { startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export const PasskeySignInButton: React.FC<{ mode?: "signin" | "signup"; email?: string }> = ({ mode = "signin", email }) => {
  const nav = useNavigate();
  const auth = useAuth();
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try { setSupported(browserSupportsWebAuthn()); } catch { setSupported(false); }
    api.get("/passkey/config")
      .then((r) => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(true));
  }, []);

  if (!supported || !enabled) return null;

  const click = async () => {
    setBusy(true);
    try {
      const beginBody = { origin: window.location.origin, ...(email ? { email } : {}) };
      const { data: begin } = await api.post("/passkey/authentication/begin", beginBody);
      const credential = await startAuthentication({ optionsJSON: begin.optionsJSON });
      const { data } = await api.post("/passkey/authentication/complete", {
        ceremonyId: begin.ceremonyId,
        credential,
        origin: window.location.origin,
      });
      localStorage.setItem("socibaba_token", data.token);
      if ((auth as any).loginWithToken) {
        await (auth as any).loginWithToken(data.token, data.user);
      }
      toast.success(`Welcome back, ${data.user?.name || "friend"}!`);
      nav("/dashboard");
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Passkey sign-in failed";
      if (typeof msg === "string" && msg.toLowerCase().includes("cancel")) {
        // User cancelled the platform dialog — stay silent.
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      data-testid={`passkey-signin-${mode}`}
      className="w-full h-10 flex items-center justify-center gap-2 rounded-full border border-[color:var(--sg-border)] bg-white dark:bg-[color:var(--sg-card)] text-[#0F172A] dark:text-[color:var(--sg-fg)] font-medium text-sm hover:border-[color:var(--sg-primary)] transition-colors disabled:opacity-60"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4 text-[color:var(--sg-primary)]" />}
      <span>{mode === "signup" ? "Sign up with passkey" : "Sign in with passkey"}</span>
    </button>
  );
};

export default PasskeySignInButton;
